import { z } from "zod";
import { STAGE_LABELS, STAGE_ORDER } from "./enums.js";

// A configurable pipeline is an ordered list of stage definitions. Each stage carries a stable
// `key` (what candidate_searches.stage stores) and a human `label` (what the UI renders). The
// default ladder uses the legacy Stage enum values as keys so existing data keeps working; a role
// can rename labels, add custom stages (generated keys) and reorder — but ONLY pre-launch. Once a
// role goes live the pipeline locks (see searches.launchedAt), so stage keys never change under a
// candidate's feet and no migration is ever needed.

export const StageDefSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1).max(40),
});
export type StageDef = z.infer<typeof StageDefSchema>;

/** The dev-built default ladder, as StageDefs — the starting point for every new role. */
export const DEFAULT_PIPELINE: StageDef[] = STAGE_ORDER.map((key) => ({ key, label: STAGE_LABELS[key] }));

/** A role's effective ladder: its own config when set & non-empty, else the default ladder. */
export function resolvePipeline(config: StageDef[] | null | undefined): StageDef[] {
  return config && config.length > 0 ? config : DEFAULT_PIPELINE;
}

/** Position of a stage key within a ladder: 0-based index, total length, and resolved label.
 *  Unknown keys (e.g. legacy data outside the ladder) fall back gracefully. */
export function stageMeta(ladder: StageDef[], key: string): { label: string; index: number; total: number } {
  const index = ladder.findIndex((s) => s.key === key);
  const def = index >= 0 ? ladder[index] : undefined;
  return { label: def?.label ?? prettifyKey(key), index, total: ladder.length };
}

/** Turn an arbitrary stage key into a readable label as a last resort (custom keys with no def). */
export function prettifyKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

/** Slugify a label into a key candidate (custom stages). Uniqueness is the caller's job. */
export function slugifyStageKey(label: string): string {
  const base = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "stage";
}

/** A unique stage key for a new custom stage, disambiguated against the keys already in use. */
export function makeStageKey(label: string, existing: Iterable<string>): string {
  const used = new Set(existing);
  const base = slugifyStageKey(label);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

/** Validation for a proposed pipeline: ≥1 stage, unique keys, unique non-empty labels. */
export const PipelineConfigSchema = z
  .object({ stages: z.array(StageDefSchema).min(1, "A pipeline needs at least one stage") })
  .superRefine((val, ctx) => {
    const keys = new Set<string>();
    const labels = new Set<string>();
    for (const s of val.stages) {
      const k = s.key;
      const l = s.label.trim().toLowerCase();
      if (keys.has(k)) ctx.addIssue({ code: "custom", message: `Duplicate stage key: ${k}`, path: ["stages"] });
      if (labels.has(l))
        ctx.addIssue({ code: "custom", message: `Duplicate stage name: ${s.label}`, path: ["stages"] });
      keys.add(k);
      labels.add(l);
    }
  });
export type PipelineConfig = z.infer<typeof PipelineConfigSchema>;
